import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import MonthlyStatsWidget from './MonthlyStatsWidget';

/**
 * OutreachAnalytics
 *
 * Content for the "Analytics" sub-tab in OutreachPage. Two sections:
 *   1. Overall MonthlyStatsWidget (user-level rollup) at the top
 *   2. Per-campaign breakdown table below — messages this month vs.
 *      last month, all-time messages, and stubs for open-rate and
 *      reply-rate until email tracking is wired.
 *
 * The component is self-contained; drop it inside OutreachPage's tab
 * fork where the Tasks tab currently renders <TaskInbox />.
 */

// Percentage delta from prev → curr. Returns null for undefined shape.
function deltaPct(curr, prev) {
  if (curr == null || prev == null) return null;
  if (prev === 0) return curr === 0 ? 0 : null; // undefined delta
  return Math.round(((curr - prev) / prev) * 100);
}

function DeltaBadge({ current, previous }) {
  const pct = deltaPct(current, previous);
  if (pct === null) {
    return <span className="text-[10px] text-gray-400">—</span>;
  }
  if (pct === 0) {
    return <span className="text-[10px] text-gray-500 font-medium">0%</span>;
  }
  const up = pct > 0;
  return (
    <span className={`text-[10px] font-medium ${up ? 'text-emerald-600' : 'text-red-600'}`}>
      {up ? '+' : ''}{pct}%
    </span>
  );
}

export default function OutreachAnalytics() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [unavailable, setUnavailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/dashboard/campaign-analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error('[banyak-outreach-analytics] fetch failed', res.status);
          setError('Failed to load');
          return;
        }
        const json = await res.json();
        setCampaigns(json.campaigns || []);
        setUnavailable(json.unavailable_metrics || []);
      } catch (e) {
        console.error('[banyak-outreach-analytics] fetch threw', e);
        setError('Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openStub = unavailable.includes('open_rate');
  const replyStub = unavailable.includes('reply_rate');

  return (
    <div className="space-y-4">
      {/* Overall stats — reuses the same widget the Dashboard uses so
          the numbers line up if the user cross-checks. */}
      <MonthlyStatsWidget />

      {/* Per-campaign breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">By Campaign</h3>
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold">
            Messages Sent
          </span>
        </div>

        {loading ? (
          <div className="py-8 text-center text-xs text-gray-400">Loading campaigns…</div>
        ) : error ? (
          <div className="py-8 text-center text-xs text-gray-400">{error}</div>
        ) : campaigns.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400">
            No campaigns yet. Create one from the Campaigns tab.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Campaign</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">This Month</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Δ</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">All Time</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Pending</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                    Open Rate{openStub && <span className="ml-1 text-[9px] text-gray-400 font-normal normal-case tracking-normal">(pending)</span>}
                  </th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                    Reply Rate{replyStub && <span className="ml-1 text-[9px] text-gray-400 font-normal normal-case tracking-normal">(pending)</span>}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/outreach/campaign/${c.id}`)}
                    className="hover:bg-emerald-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          c.status === 'active' ? 'bg-emerald-500' :
                          c.status === 'paused' ? 'bg-amber-500' :
                          c.status === 'draft'  ? 'bg-gray-400' :
                                                  'bg-gray-300'
                        }`} />
                        <span className="text-sm font-medium text-gray-900 truncate">{c.name}</span>
                      </div>
                    </td>
                    <td className="text-right px-3 py-2.5 tabular-nums text-gray-900 font-semibold">
                      {(c.messages_this_month || 0).toLocaleString()}
                    </td>
                    <td className="text-right px-3 py-2.5">
                      <DeltaBadge current={c.messages_this_month} previous={c.messages_last_month} />
                    </td>
                    <td className="text-right px-3 py-2.5 tabular-nums text-gray-600">
                      {(c.messages_all_time || 0).toLocaleString()}
                    </td>
                    <td className="text-right px-3 py-2.5 tabular-nums text-gray-500">
                      {(c.messages_pending || 0).toLocaleString()}
                    </td>
                    <td className="text-right px-3 py-2.5 text-gray-300 text-xs">
                      —
                    </td>
                    <td className="text-right px-3 py-2.5 text-gray-300 text-xs">
                      —
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(openStub || replyStub) && (
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500">
            Open + Reply rates activate once email tracking is integrated
            (SendGrid / Mailgun webhooks or a tracking pixel).
          </div>
        )}
      </div>
    </div>
  );
}
